import { PathSessionType } from 'src/apps/Path/types';
import { ROUTES } from 'src/constants/routes.const';
import { getAprenderLearnSessionUrl } from 'src/utils/getAprenderLearnSessionUrl';
import { getBuildingSentencesSessionUrl } from 'src/utils/getBuildingSentencesSessionUrl';
import { getPatternSessionUrl } from 'src/utils/getPatternSessionUrl';
import { getGrammarSessionUrl } from 'src/utils/getGrammarSessionUrl';
import { stringifyUrl } from 'src/utils/stringifyUrl';

/**
 * Path session types that launch an AI buddy dialog.
 * Sentence path nodes use BuildingSentences (`/aprender/building-sentences`).
 * The cloze `/aprender/cloze` flow is only for checkpoint assessments.
 * AI Conversation uses Membot via getPathAiConversationSessionUrl instead.
 * Grammar uses `/aprender/grammar`.
 * Patterns uses `/aprender/patterns`.
 */
const BUDDY_TYPE_BY_SESSION_TYPE: Partial<Record<PathSessionType, string>> = {
  pronunciation: 'pronunciation',
};

export type PathSessionUrlOptions = {
  pathwayNodeId?: number;
  /** When true, omit pathway_node_id so completing the session does not advance the path. */
  forReview?: boolean;
};

export const getPathSessionUrl = (
  languagePairId: number,
  sessionType: PathSessionType | string,
  options: PathSessionUrlOptions = {},
): string => {
  const pathwayNodeId = options.forReview ? undefined : options.pathwayNodeId;

  if (sessionType === 'immersion') {
    // Prefer getPathImmersionSessionUrl for a direct /media launch.
    // This immerse-list URL is the sync fallback when no video can be picked.
    const params: Record<string, string | number> = {
      source: 'path',
      back: ROUTES.path,
    };
    if (pathwayNodeId !== undefined) {
      params.pathway_node_id = pathwayNodeId;
    }
    return stringifyUrl(ROUTES.video.immerse, params);
  }

  if (sessionType === 'aiConversation') {
    // Prefer getPathAiConversationSessionUrl for a direct /membot launch.
    // This speak URL is the sync fallback when no mission can be picked.
    return ROUTES.speak;
  }

  if (sessionType === 'review') {
    // Prefer getPathReviewSessionUrl, which falls back to Learn when nothing is due.
    const params: Record<string, string | number> = {
      language_pair_id: languagePairId,
      source: 'path',
      back: ROUTES.path,
    };
    if (pathwayNodeId !== undefined) {
      params.pathway_node_id = pathwayNodeId;
    }
    return stringifyUrl(ROUTES.aprender.review, params);
  }

  if (sessionType === 'grammar') {
    return getGrammarSessionUrl(languagePairId, {
      source: 'path',
      back: ROUTES.path,
      pathwayNodeId,
    });
  }

  if (sessionType === 'conjugation') {
    return getPatternSessionUrl(languagePairId, {
      source: 'path',
      back: ROUTES.path,
      pathwayNodeId,
    });
  }

  if (sessionType === 'sentence') {
    return getBuildingSentencesSessionUrl(languagePairId, {
      source: 'path',
      back: ROUTES.path,
      pathwayNodeId,
    });
  }

  const buddyType = BUDDY_TYPE_BY_SESSION_TYPE[sessionType as PathSessionType];
  if (buddyType) {
    const params: Record<string, string | number> = {
      type: buddyType,
      source: 'path',
      back: ROUTES.path,
    };
    if (pathwayNodeId !== undefined) {
      params.pathway_node_id = pathwayNodeId;
    }
    return stringifyUrl(ROUTES.ai.dialog, params);
  }

  return getAprenderLearnSessionUrl(languagePairId, {
    source: 'path',
    pathwayNodeId,
    back: ROUTES.path,
  });
};
